import {
  ALLOWED_ATTRIBUTE_VALUES,
  ALLOWED_TAGS,
  NESTING_RULES,
  OPTIONAL_ATTRIBUTES,
  REQUIRED_ATTRIBUTES,
  SELF_CLOSING_TAGS,
  SELF_NESTING_FORBIDDEN,
  SAY_AS_CONTENT_PATTERNS,
} from "shared/constants/ssmlValidator";
import {
  AllowedAttributeValues,
  ValidationError,
  ValidationResult,
  CumulativeErrors,
  ValidatorOptions,
} from "shared/models/ssmlValidator";

// Helper function to get error location
function getErrorLocation(
  fullText: string,
  position: number,
  errorLength: number = 1
): {
  line: number;
  column: number;
  preview: string;
  errorIndexInPreview: number;
  errorLength: number;
} {
  const textUpToError = fullText.substring(0, position);
  const lines = textUpToError.split("\n");
  const line = lines.length;
  const column = lines[lines.length - 1].length + 1;

  // Preview generation
  const contextLength = 30;
  const start = Math.max(0, position - contextLength);
  const end = Math.min(fullText.length, position + contextLength + 10);

  let preview = fullText.substring(start, end);

  // Calculate where the error starts
  let errorIndexInPreview = position - start;

  if (start > 0) {
    preview = "..." + preview;
    errorIndexInPreview += 3;
  }
  if (end < fullText.length) {
    preview = preview + "...";
  }

  if (errorLength === 1) {
    const remainingPreview = preview.substring(errorIndexInPreview);
    const match = remainingPreview.match(/^[^\s>=]+/);
    if (match) {
      errorLength = match[0].length;
    }
  }

  return { line, column, preview, errorIndexInPreview, errorLength };
}

//Helper function to validate Say-As tag content
export function validateSayAsContent(
  ssmlString: string,
  enabled: boolean = false
): ValidationError[] {
  if (!enabled) return [];

  const errors: ValidationError[] = [];
  const sayAsRegex = /<say-as\s+([^>]*)>(.*?)<\/say-as>/gi;
  let match: RegExpExecArray | null;

  while ((match = sayAsRegex.exec(ssmlString)) !== null) {
    const attributesString = match[1];
    const content = match[2].trim();
    const position = match.index;

    const interpretAsMatch = /interpret-as\s*=\s*['"]([^'"]+)['"]/i.exec(
      attributesString
    );
    const formatMatch = /format\s*=\s*['"]([^'"]+)['"]/i.exec(attributesString);

    if (!interpretAsMatch) continue;

    const interpretAs = interpretAsMatch[1];
    const format = formatMatch ? formatMatch[1] : undefined;

    const rule = SAY_AS_CONTENT_PATTERNS[interpretAs];
    if (!rule) continue;

    let isValid = true;

    if (rule.pattern) {
      isValid = rule.pattern.test(content);
    } else if (rule.validator) {
      isValid = rule.validator(content, format);
    }

    if (!isValid) {
      const contentOffset = match[0].indexOf(content);
      const exactContentPos = position + contentOffset;
      const loc = getErrorLocation(ssmlString, exactContentPos, content.length);

      errors.push({
        type: "SAY_AS_CONTENT_MISMATCH",
        message: `Content '${content}' in <say-as interpret-as='${interpretAs}'${format ? ` format='${format}'` : ""}> does not match expected format. ${rule.description}`,
        position: exactContentPos,
        line: loc.line,
        column: loc.column,
        preview: loc.preview,
        errorIndexInPreview: loc.errorIndexInPreview,
        errorLength: loc.errorLength,
      });
    }
  }

  return errors;
}

//Helper function to categorize the errors.
function categorizeError(
  errorType: string
): keyof Omit<CumulativeErrors, "messages"> | null {
  if (errorType === "INVALID_NESTING" || errorType === "INVALID_SELF_NESTING") {
    return "nestingErrors";
  }

  if (
    errorType === "UNCLOSED_TAG" ||
    errorType === "UNMATCHED_CLOSING" ||
    errorType === "MISMATCHED_TAG" ||
    errorType === "BRACKET_MISMATCH" ||
    errorType === "CLOSING_TAG_HAS_ATTRIBUTES"
  ) {
    return "tagsNotClosed";
  }

  if (
    errorType === "MISSING_ATTRIBUTE" ||
    errorType === "INVALID_ATTRIBUTE_VALUE" ||
    errorType === "MISSING_OPTIONAL_ATTRIBUTE" ||
    errorType === "MALFORMED_ATTRIBUTE" ||
    errorType === "UNKNOWN_ATTRIBUTE" ||
    errorType === "SAY_AS_CONTENT_MISMATCH"
  ) {
    return "wrongAttributes";
  }

  if (errorType === "UNKNOWN_TAG") {
    return "unknownTags";
  }

  return null;
}

//Generate the custom errors
function generateCumulativeMessages(
  cumulative: Omit<CumulativeErrors, "messages">,
  t?: (key: string) => string
): string[] {
  const messages: string[] = [];
  const hasNesting = cumulative.nestingErrors > 0;
  const hasClosure = cumulative.tagsNotClosed > 0;
  const hasAttributes = cumulative.wrongAttributes > 0;
  const hasUnknown = cumulative.unknownTags > 0;

  const errorTypeCount = [
    hasNesting,
    hasClosure,
    hasAttributes,
    hasUnknown,
  ].filter(Boolean).length;

  let translationKey: string;

  if (errorTypeCount === 4) {
    translationKey = "tts.customErrors.allErrorTypes";
  } else if (errorTypeCount === 3) {
    if (!hasNesting) {
      translationKey = "tts.customErrors.closureAttributesUnknown";
    } else if (!hasClosure) {
      translationKey = "tts.customErrors.nestingAttributesUnknown";
    } else if (!hasAttributes) {
      translationKey = "tts.customErrors.nestingClosureUnknown";
    } else {
      translationKey = "tts.customErrors.nestingClosureAttributes";
    }
  } else if (errorTypeCount === 2) {
    if (hasNesting && hasClosure) {
      translationKey = "tts.customErrors.nestingAndClosure";
    } else if (hasNesting && hasAttributes) {
      translationKey = "tts.customErrors.nestingAndAttributes";
    } else if (hasNesting && hasUnknown) {
      translationKey = "tts.customErrors.nestingAndUnknown";
    } else if (hasClosure && hasAttributes) {
      translationKey = "tts.customErrors.closureAndAttributes";
    } else if (hasClosure && hasUnknown) {
      translationKey = "tts.customErrors.closureAndUnknown";
    } else {
      translationKey = "tts.customErrors.attributesAndUnknown";
    }
  } else {
    if (hasNesting) {
      translationKey = "tts.customErrors.nestingErrors";
    } else if (hasClosure) {
      translationKey = "tts.customErrors.tagsNotClosed";
    } else if (hasAttributes) {
      translationKey = "tts.customErrors.wrongAttributes";
    } else {
      translationKey = "tts.customErrors.unknownTags";
    }
  }

  messages.push(t ? t(translationKey) : translationKey);

  return messages;
}

// Function to calculate cumulative errors with messages
function calculateCumulativeErrors(
  errors: ValidationError[],
  t?: (key: string) => string
): CumulativeErrors {
  const cumulative = {
    nestingErrors: 0,
    tagsNotClosed: 0,
    wrongAttributes: 0,
    unknownTags: 0,
  };

  for (const error of errors) {
    const category = categorizeError(error.type);
    if (category) {
      cumulative[category]++;
    }
  }

  const messages = generateCumulativeMessages(cumulative, t);

  return {
    ...cumulative,
    messages,
  };
}

function isSelfClosingTag(fullTag: string): boolean {
  return /\/\s*>$/.test(fullTag);
}

function isValidPitch(value: string): boolean {
  const val = value.toLowerCase();
  const allowedPitchValues = ALLOWED_ATTRIBUTE_VALUES["prosody"]["pitch"];
  const semitonePattern = /^[+-]?\d+(\.\d+)?st$/;
  const frequencyPattern = /^[+-]?\d+(\.\d+)?hz$/;

  return (
    allowedPitchValues.includes(val) ||
    semitonePattern.test(val) ||
    frequencyPattern.test(val)
  );
}

function isValidRate(value: string): boolean {
  const val = value.toLowerCase();
  const allowedRateValues = ALLOWED_ATTRIBUTE_VALUES["prosody"]["rate"];
  const percentagePattern = /^\d+(\.\d+)?%$/;

  return allowedRateValues.includes(val) || percentagePattern.test(value);
}

function isValidContour(value: string): boolean {
  const trimmedValue = value.trim();

  // Reject empty or whitespace-only strings
  if (!trimmedValue) {
    return false;
  }

  // Match all valid pairs
  const pairPattern = /\(\s*(\d+(?:\.\d+)?)\s*%\s*,\s*([^)]+?)\s*\)/g;
  const pairs: {
    percentage: string;
    pitch: string;
    startIndex: number;
    endIndex: number;
  }[] = [];
  let match: RegExpExecArray | null;

  while ((match = pairPattern.exec(trimmedValue)) !== null) {
    pairs.push({
      percentage: match[1],
      pitch: match[2].trim(),
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  if (pairs.length === 0) {
    return false;
  }

  let expectedPosition = 0;
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i];
    const beforePair = trimmedValue.slice(expectedPosition, pair.startIndex);

    if (beforePair.trim() !== "") {
      return false;
    }

    expectedPosition = pair.endIndex;
  }

  const afterLastPair = trimmedValue.slice(expectedPosition);
  if (afterLastPair.trim() !== "") {
    return false;
  }

  // Validate each pair
  for (const pair of pairs) {
    const percentage = parseFloat(pair.percentage);
    if (percentage < 0 || percentage > 100 || isNaN(percentage)) {
      return false;
    }

    const pitch = pair.pitch;
    const pitchLower = pitch.toLowerCase();
    const validEnums = ["x-low", "low", "medium", "default", "high", "x-high"];
    const semitonePattern = /^[+-]?\d+(\.\d+)?st$/i;
    const frequencyPattern = /^[+-]?\d+(\.\d+)?hz$/i;
    const percentagePattern = /^[+-]\d+(\.\d+)?%$/;

    const isValidPitchValue =
      validEnums.includes(pitchLower) ||
      semitonePattern.test(pitch) ||
      frequencyPattern.test(pitch) ||
      percentagePattern.test(pitch);

    if (!isValidPitchValue) {
      return false;
    }
  }

  return true;
}

function validateNesting(
  parentTag: string,
  childTag: string,
  position: number,
  stack: Array<{ name: string; position: number }>,
  ssmlString: string
): ValidationError | null {
  if (SELF_NESTING_FORBIDDEN.has(childTag)) {
    const alreadyNested = stack.some((item) => item.name === childTag);
    if (alreadyNested) {
      const loc = getErrorLocation(ssmlString, position, childTag.length + 1); // Highlight <tagName
      return {
        type: "INVALID_SELF_NESTING",
        message: `Tag <${childTag}> cannot be nested within itself`,
        position,
        line: loc.line,
        column: loc.column,
        preview: loc.preview,
        errorIndexInPreview: loc.errorIndexInPreview,
        errorLength: loc.errorLength,
      };
    }
  }

  const allowedChildren = NESTING_RULES[parentTag];

  if (!allowedChildren) return null;

  if (allowedChildren === "none") {
    const loc = getErrorLocation(ssmlString, position, childTag.length + 1);
    return {
      type: "INVALID_NESTING",
      message: `Tag <${parentTag}> cannot contain child tags. Found <${childTag}>.`,
      position,
      line: loc.line,
      column: loc.column,
      preview: loc.preview,
      errorIndexInPreview: loc.errorIndexInPreview,
      errorLength: loc.errorLength,
    };
  }

  if (allowedChildren === "any") return null;

  if (!allowedChildren.includes(childTag)) {
    const loc = getErrorLocation(ssmlString, position, childTag.length + 1);
    return {
      type: "INVALID_NESTING",
      message: `Tag <${parentTag}> cannot contain <${childTag}>. Allowed children: ${allowedChildren.join(", ")}.`,
      position,
      line: loc.line,
      column: loc.column,
      preview: loc.preview,
      errorIndexInPreview: loc.errorIndexInPreview,
      errorLength: loc.errorLength,
    };
  }

  return null;
}

function validateTagHierarchy(ssmlString: string): ValidationError[] {
  const errors: ValidationError[] = [];
  const stack: Array<{ name: string; position: number }> = [];
  const tagRegex = /<\/?([a-z-]+)([^>]*)>/gi;
  let match: RegExpExecArray | null;

  while ((match = tagRegex.exec(ssmlString)) !== null) {
    const fullTag = match[0];
    const tagName = match[1];
    const position = match.index;
    const isClosing = fullTag.startsWith("</");
    const isSelfClosing = isSelfClosingTag(fullTag);

    if (isClosing) {
      if (stack.length > 0 && stack[stack.length - 1].name === tagName) {
        stack.pop();
      }
      continue;
    }

    if (!isSelfClosing && stack.length > 0) {
      const parent = stack[stack.length - 1];
      const nestingError = validateNesting(
        parent.name,
        tagName,
        position,
        stack,
        ssmlString
      );
      if (nestingError) {
        errors.push(nestingError);
      }
    } else if (!isSelfClosing && stack.length === 0) {
      if (SELF_NESTING_FORBIDDEN.has(tagName)) {
        const alreadyExists = stack.some((item) => item.name === tagName);
        if (alreadyExists) {
          const loc = getErrorLocation(
            ssmlString,
            position,
            tagName.length + 1
          );
          errors.push({
            type: "INVALID_SELF_NESTING",
            message: `Tag <${tagName}> cannot be nested within itself`,
            position,
            line: loc.line,
            column: loc.column,
            preview: loc.preview,
            errorIndexInPreview: loc.errorIndexInPreview,
            errorLength: loc.errorLength,
          });
        }
      }
    }

    if (!isSelfClosing) {
      stack.push({ name: tagName, position });
    }
  }

  return errors;
}

// Helper function to extract all attributes from a tag
export function extractAttributes(
  attributesString: string
): Array<{ name: string; value: string }> {
  const attributes: Array<{ name: string; value: string }> = [];
  // Match attribute="value" or attribute='value'
  const attrPattern = /([a-z-]+)\s*=\s*"([^"]*)"|([a-z-]+)\s*=\s*'([^']*)'/gi;
  let match: RegExpExecArray | null;

  while ((match = attrPattern.exec(attributesString)) !== null) {
    if (match[1]) {
      // Double quote match
      attributes.push({
        name: match[1],
        value: match[2],
      });
    } else {
      // Single quote match
      attributes.push({
        name: match[3],
        value: match[4],
      });
    }
  }

  return attributes;
}

// Sanitization Function
export function sanitizeInvalidTags(ssmlString: string): string {
  const tagRegex = /<\/?([a-z-]+)([^>]*)>/gi;
  const stack: { name: string; valid: boolean }[] = [];
  let result = "";
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tagRegex.exec(ssmlString)) !== null) {
    const tagStart = match.index;
    const tagEnd = tagRegex.lastIndex;
    const tagName = match[1];
    const fullTag = match[0];
    const attributesString = match[2];
    const isClosingTag = fullTag.startsWith("</");
    const isSelfClosing = /\/\s*>$/.test(fullTag);

    result += ssmlString.slice(lastIndex, tagStart);

    const isTagNameValid = ALLOWED_TAGS.includes(tagName);
    let isTagValid = isTagNameValid;

    if (isTagNameValid && !isClosingTag) {
      const attributes = extractAttributes(attributesString);
      const attrMap = new Map(attributes.map((a) => [a.name, a.value]));

      const required = REQUIRED_ATTRIBUTES[tagName] || [];
      for (const attr of required) {
        // accent is optional for lang, absence means "none"
        if (tagName === "lang" && attr === "accent") continue;

        if (!attrMap.has(attr)) {
          isTagValid = false;
          break;
        }
      }

      if (isTagValid) {
        for (const { name: attrName, value: attrValue } of attributes) {
          const allowedValues = ALLOWED_ATTRIBUTE_VALUES?.[tagName]?.[attrName];

          if (allowedValues) {
            if (tagName === "prosody" && attrName === "pitch") {
              if (!isValidPitch(attrValue)) {
                isTagValid = false;
                break;
              }
            } else if (tagName === "prosody" && attrName === "rate") {
              if (!isValidRate(attrValue)) {
                isTagValid = false;
                break;
              }
            } else if (tagName === "prosody" && attrName === "contour") {
              if (!isValidContour(attrValue)) {
                isTagValid = false;
                break;
              }
            } else if (!allowedValues.includes(attrValue)) {
              isTagValid = false;
              break;
            }
          }
        }
      }
    }

    if (isClosingTag) {
      const lastTag = stack.length > 0 ? stack[stack.length - 1] : null;
      if (lastTag && lastTag.name === tagName) {
        const wasValid = lastTag.valid;
        stack.pop();
        if (wasValid) {
          result += fullTag;
        }
      }
    } else if (!isSelfClosing) {
      stack.push({ name: tagName, valid: isTagValid });
      if (isTagValid) {
        result += fullTag;
      }
    } else {
      if (isTagValid) {
        result += fullTag;
      }
    }

    lastIndex = tagEnd;
  }

  result += ssmlString.slice(lastIndex);
  return result;
}

// Main Validation Function
export function validateSSML(
  ssmlString: string,
  sanitizeInvalid: boolean = false,
  t?: (key: string) => string,
  options?: ValidatorOptions
): ValidationResult {
  const speakMatch = /^\s*<speak\b[^>]*>([\s\S]*?)<\/speak>/i.exec(ssmlString);

  if (speakMatch) {
    ssmlString = speakMatch[1];
  } else {
    ssmlString = ssmlString
      .replace(/^\s*<speak\b[^>]*>/i, "")
      .replace(/<\/speak>[\s\S]*$/i, "");
  }

  const normalizedSSML = ssmlString.replace(/[""]/g, '"').replace(/['']/g, "'");

  // Build allowed values, merging dynamic voice data if available
  const effectiveAllowedValues: AllowedAttributeValues = {
    ...ALLOWED_ATTRIBUTE_VALUES,
    voice: {
      name:
        options?.dynamicAllowedValues?.voiceNames ??
        ALLOWED_ATTRIBUTE_VALUES.voice.name,
      language:
        options?.dynamicAllowedValues?.languageValues ??
        ALLOWED_ATTRIBUTE_VALUES.voice.language,
    },
    style: {
      ...ALLOWED_ATTRIBUTE_VALUES.style,
      name:
        options?.dynamicAllowedValues?.styleNames ??
        ALLOWED_ATTRIBUTE_VALUES.style.name,
    },
    lang: {
      ...ALLOWED_ATTRIBUTE_VALUES.lang,
      language:
        options?.dynamicAllowedValues?.languageValues ??
        ALLOWED_ATTRIBUTE_VALUES.lang.language,
      accent: ALLOWED_ATTRIBUTE_VALUES.lang.accent,
    },
    "say-as": {
      ...ALLOWED_ATTRIBUTE_VALUES["say-as"],
      language:
        options?.dynamicAllowedValues?.languageValues ??
        ALLOWED_ATTRIBUTE_VALUES["say-as"].language,
    },
  };

  const errors: ValidationError[] = [];
  const stack: Array<{ name: string; position: number }> = [];
  const tagRegex = /<\/?([a-z-]+)([^>]*)>/gi;
  let match: RegExpExecArray | null;

  while ((match = tagRegex.exec(normalizedSSML)) !== null) {
    const fulltag = match[0];
    const tagName = match[1];
    const attributesString = match[2];
    const position = match.index;
    const isClosingTag = fulltag.startsWith("</");
    const isSelfClosing =
      /\/\s*>$/.test(fulltag) && SELF_CLOSING_TAGS.includes(tagName);

    /* For now we are checking the attributes of the phoneme tag and not skipping it, enable this code block to skip validation for phoneme tags */
    // if (tagName === 'phoneme') {
    //   continue;
    // }

    if (!ALLOWED_TAGS.includes(tagName)) {
      const loc = getErrorLocation(
        normalizedSSML,
        position,
        tagName.length + 1
      );
      errors.push({
        type: "UNKNOWN_TAG",
        message: `Unknown Tag <${tagName}>`,
        position,
        line: loc.line,
        column: loc.column,
        preview: loc.preview,
        errorIndexInPreview: loc.errorIndexInPreview,
        errorLength: loc.errorLength,
      });
      continue;
    }

    if (!isClosingTag && !isSelfClosing) {
      const doubleQuotes = (attributesString.match(/"/g) || []).length;
      const singleQuotes = (attributesString.match(/'/g) || []).length;

      if (doubleQuotes % 2 !== 0) {
        const loc = getErrorLocation(normalizedSSML, position);
        errors.push({
          type: "MALFORMED_ATTRIBUTE",
          message: `Tag <${tagName}> has unclosed double quote in attributes`,
          position,
          line: loc.line,
          column: loc.column,
          preview: loc.preview,
          errorIndexInPreview: loc.errorIndexInPreview,
          errorLength: loc.errorLength,
        });
      }

      if (singleQuotes % 2 !== 0) {
        const loc = getErrorLocation(normalizedSSML, position);
        errors.push({
          type: "MALFORMED_ATTRIBUTE",
          message: `Tag <${tagName}> has unclosed single quote in attributes`,
          position,
          line: loc.line,
          column: loc.column,
          preview: loc.preview,
          errorIndexInPreview: loc.errorIndexInPreview,
          errorLength: loc.errorLength,
        });
      }
    }

    if (isClosingTag) {
      if (attributesString.trim().length > 0) {
        const loc = getErrorLocation(normalizedSSML, position);
        errors.push({
          type: "CLOSING_TAG_HAS_ATTRIBUTES",
          message: `Closing tag </${tagName}> must not contain attributes or extra text`,
          position,
          line: loc.line,
          column: loc.column,
          preview: loc.preview,
          errorIndexInPreview: loc.errorIndexInPreview,
          errorLength: loc.errorLength,
        });
      }
      if (stack.length === 0) {
        const loc = getErrorLocation(normalizedSSML, position);
        errors.push({
          type: "UNMATCHED_CLOSING",
          message: `Unmatched Closing tag: </${tagName}>`,
          position,
          line: loc.line,
          column: loc.column,
          preview: loc.preview,
          errorIndexInPreview: loc.errorIndexInPreview,
          errorLength: loc.errorLength,
        });
      } else if (stack[stack.length - 1].name !== tagName) {
        const loc = getErrorLocation(normalizedSSML, position);
        errors.push({
          type: "MISMATCHED_TAG",
          message: `Expected </${stack[stack.length - 1].name}>, found </${tagName}>`,
          position,
          line: loc.line,
          column: loc.column,
          preview: loc.preview,
          errorIndexInPreview: loc.errorIndexInPreview,
          errorLength: loc.errorLength,
        });
      } else {
        stack.pop();
      }
    } else {
      if (!isSelfClosing) {
        stack.push({ name: tagName, position });
      }

      const attributes = extractAttributes(attributesString);
      const attrMap = new Map(attributes.map((a) => [a.name, a.value]));

      //Check for unknown attribute names
      const required = REQUIRED_ATTRIBUTES[tagName] || [];
      const optional = OPTIONAL_ATTRIBUTES[tagName] || [];
      const allAllowedAttrs = new Set([...required, ...optional]);

      for (const { name: attrName } of attributes) {
        if (!allAllowedAttrs.has(attrName)) {
          const offsetInTag = fulltag.indexOf(attrName);
          const exactAttrPosition = position + offsetInTag;

          const loc = getErrorLocation(
            normalizedSSML,
            exactAttrPosition,
            attrName.length
          );

          errors.push({
            type: "UNKNOWN_ATTRIBUTE",
            message: `Unknown attribute '${attrName}' for tag <${tagName}>. Allowed: ${Array.from(allAllowedAttrs).join(", ")}`,
            position: exactAttrPosition,
            line: loc.line,
            column: loc.column,
            preview: loc.preview,
            errorIndexInPreview: loc.errorIndexInPreview,
            errorLength: loc.errorLength,
          });
        }
      }
      // Required attributes
      if (REQUIRED_ATTRIBUTES[tagName]) {
        for (const attr of REQUIRED_ATTRIBUTES[tagName]) {
          // accent is optional for lang, absence means "none"
          if (tagName === "lang" && attr === "accent" && !attrMap.has(attr)) {
            continue;
          }

          if (!attrMap.has(attr)) {
            const loc = getErrorLocation(
              normalizedSSML,
              position,
              tagName.length + 1
            ); // Highlight <tagName
            errors.push({
              type: "MISSING_ATTRIBUTE",
              message: `Tag <${tagName}> requires attribute '${attr}'`,
              position,
              line: loc.line,
              column: loc.column,
              preview: loc.preview,
              errorIndexInPreview: loc.errorIndexInPreview,
              errorLength: loc.errorLength,
            });
          } else {
            if (
              effectiveAllowedValues[tagName] &&
              effectiveAllowedValues[tagName][attr]
            ) {
              const attributeValue = attrMap.get(attr)!;
              const allowedValues = effectiveAllowedValues[tagName][attr];

              if (!allowedValues.includes(attributeValue)) {
                // Precise Highlighting: Value
                const offsetInTag = fulltag.indexOf(attributeValue);
                const exactValPosition = position + offsetInTag;
                const loc = getErrorLocation(
                  normalizedSSML,
                  exactValPosition,
                  attributeValue.length
                );

                errors.push({
                  type: "INVALID_ATTRIBUTE_VALUE",
                  message: `Invalid value '${attributeValue}' for attribute '${attr}'. Allowed values: ${allowedValues.join(", ")}`,
                  position: exactValPosition,
                  line: loc.line,
                  column: loc.column,
                  preview: loc.preview,
                  errorIndexInPreview: loc.errorIndexInPreview,
                  errorLength: loc.errorLength,
                });
              }
            }
          }
        }
      }
      // Optional attributes
      if (OPTIONAL_ATTRIBUTES[tagName]) {
        const optionalAttrs = OPTIONAL_ATTRIBUTES[tagName];
        const presentOptionals = optionalAttrs.filter((attr: string) =>
          attrMap.has(attr)
        );

        if (presentOptionals.length === 0) {
          // lang's accent is truly optional, absence means "none", so no error
          if (tagName === "lang") continue;

          const loc = getErrorLocation(
            normalizedSSML,
            position,
            tagName.length + 1
          );
          errors.push({
            type: "MISSING_OPTIONAL_ATTRIBUTE",
            message: `Tag <${tagName}> must have at least one of the optional attributes: ${optionalAttrs.join(", ")}`,
            position,
            line: loc.line,
            column: loc.column,
            preview: loc.preview,
            errorIndexInPreview: loc.errorIndexInPreview,
            errorLength: loc.errorLength,
          });
        } else {
          for (const attr of presentOptionals) {
            const attributeValue = attrMap.get(attr)!;

            if (
              effectiveAllowedValues[tagName] &&
              effectiveAllowedValues[tagName][attr]
            ) {
              const checkValue = (isValid: boolean, message: string) => {
                if (!isValid) {
                  const offsetInTag = fulltag.indexOf(attributeValue);
                  const exactValPosition = position + offsetInTag;
                  const loc = getErrorLocation(
                    normalizedSSML,
                    exactValPosition,
                    attributeValue.length
                  );
                  errors.push({
                    type: "INVALID_ATTRIBUTE_VALUE",
                    message,
                    position: exactValPosition,
                    line: loc.line,
                    column: loc.column,
                    preview: loc.preview,
                    errorIndexInPreview: loc.errorIndexInPreview,
                    errorLength: loc.errorLength,
                  });
                }
              };

              if (tagName === "prosody" && attr === "pitch") {
                checkValue(
                  isValidPitch(attributeValue),
                  `Invalid value '${attributeValue}' for optional attribute '${attr}'. Allowed values: ${ALLOWED_ATTRIBUTE_VALUES["prosody"]["pitch"].join(", ")}, or relative shifts like +2st, -1st, +50Hz, -20Hz`
                );
              } else if (tagName === "prosody" && attr === "rate") {
                checkValue(
                  isValidRate(attributeValue),
                  `Invalid value '${attributeValue}' for optional attribute '${attr}'. Allowed values: ${ALLOWED_ATTRIBUTE_VALUES["prosody"]["rate"].join(", ")}, or percentage like 50%, 100%, 150%`
                );
              } else if (tagName === "prosody" && attr === "contour") {
                checkValue(
                  isValidContour(attributeValue),
                  `Invalid value '${attributeValue}' for optional attribute '${attr}'. Format should be: (percentage,pitch) pairs like (0%,+0st) (50%,x-high) (100%,+0st)`
                );
              } else {
                const allowedValues = effectiveAllowedValues[tagName][attr];
                if (!allowedValues.includes(attributeValue)) {
                  const offsetInTag = fulltag.indexOf(attributeValue);
                  const exactValPosition = position + offsetInTag;
                  const loc = getErrorLocation(
                    normalizedSSML,
                    exactValPosition,
                    attributeValue.length
                  );
                  errors.push({
                    type: "INVALID_ATTRIBUTE_VALUE",
                    message: `Invalid value '${attributeValue}' for optional attribute '${attr}'. Allowed values: ${allowedValues.join(", ")}`,
                    position: exactValPosition,
                    line: loc.line,
                    column: loc.column,
                    preview: loc.preview,
                    errorIndexInPreview: loc.errorIndexInPreview,
                    errorLength: loc.errorLength,
                  });
                }
              }
            }
          }
        }
      }
    }
  }

  if (stack.length > 0) {
    stack.forEach((tag) => {
      const loc = getErrorLocation(
        normalizedSSML,
        tag.position,
        tag.name.length + 1
      );
      errors.push({
        type: "UNCLOSED_TAG",
        message: `Unclosed tag: <${tag.name}>`,
        position: tag.position,
        line: loc.line,
        column: loc.column,
        preview: loc.preview,
        errorIndexInPreview: loc.errorIndexInPreview,
        errorLength: loc.errorLength,
      });
    });
  }

  const openBrackets = (normalizedSSML.match(/</g) || []).length;
  const closeBrackets = (normalizedSSML.match(/>/g) || []).length;
  if (openBrackets !== closeBrackets) {
    errors.push({
      type: "BRACKET_MISMATCH",
      message: `Mismatch in angle brackets`,
    });
  }

  // Add nesting validation
  const nestingErrors = validateTagHierarchy(normalizedSSML);
  errors.push(...nestingErrors);

  const sayAsErrors = validateSayAsContent(normalizedSSML, true);
  errors.push(...sayAsErrors);

  const cumulativeErrors = calculateCumulativeErrors(errors, t);

  const isValid = errors.length === 0;
  const result: ValidationResult = {
    valid: isValid,
    errors,
    cumulativeErrors,
  };

  if (!isValid && sanitizeInvalid) {
    result.sanitizedSSML = sanitizeInvalidTags(ssmlString);
  }

  return result;
}
// Independent nesting checker
export function hasStructuralNestingErrors(ssmlString: string): boolean {
  const nestingErrors = validateTagHierarchy(ssmlString);
  return nestingErrors.length > 0;
}
